import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { ControlsHubPanelsConfig } from './controls-hub-panels-config/controls-hub-panels-config';
import ChipList from '@components-clean/core/chip-list';
import ComparisonDatasetCard from '@components-clean/core/comparison-dataset-card';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import StudyHubPanelLineCalculationSelector from '@pages-clean/admin/study/manage-hubs/central-monitoring/StudyHub-Panel-Line-Calculation-Selector';
import StudyHubSiteFields from '@pages-clean/admin/study/manage-hubs/central-monitoring/StudyHub-Site-Fields';
import StudyHubStudyFields from '@pages-clean/admin/study/manage-hubs/central-monitoring/StudyHub-Study-Fields';
import MonitoringToolkitConfig from '@pages-clean/admin/study/manage-hubs/central-monitoring/monitoring-toolkit';
import PanelsConfig from '@pages-clean/admin/study/manage-hubs/central-monitoring/panels-config';
import WidgetConfig from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config';
import { WidgetDataProps } from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config/widget-config.interface';
import { LocalStateContext } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import {
    GET_CONTROLS_HUB_WIDGET_CONFIG,
    GET_PANELS,
    GET_STUDY_DASHBOARD_CONFIGURATION_WIDGET,
} from '@queries/central-monitoring';
import {
    getPanels_OpraQueries as getPanelsResponse,
    getPanelsVariables,
    getStudyDashboardConfigWidgetsForStudy,
    getStudyDashboardConfigWidgetsForStudyVariables,
    getControlsHubConfigWidgetsForStudy,
    getControlsHubConfigWidgetsForStudyVariables,
} from '@queries/generated/central-monitoring.generated';

type CentralMonitoringType = 'StudySiteHub' | 'ControlsHub';

const ManageHubsCentralMonitoring = () => {
    const userContext = useContext(UserContext);
    const { state } = useContext(LocalStateContext);

    const { t: tAdmin } = useTranslation('admin', { keyPrefix: 'centralMonitoring' });

    const [selected, setSelected] = useState<CentralMonitoringType>('StudySiteHub');
    const studyRef = state?.selectedStudy?.id as string;
    const canEdit = userContext.user!.canWriteSystemAdminLevel!(false, false, false, true);

    const orgKey = state?.selectedOrganisation?.id;

    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges } = useUnsavedChangesContext();

    useEffect(() => {
        setSelected('StudySiteHub');
    }, []);

    const [loadConfig, { data: widgetConfigData }] = useLazyQuery<
        getStudyDashboardConfigWidgetsForStudy,
        getStudyDashboardConfigWidgetsForStudyVariables
    >(GET_STUDY_DASHBOARD_CONFIGURATION_WIDGET);
    const [getPanels, { data: getPanelsRawData }] = useLazyQuery<getPanelsResponse, getPanelsVariables>(GET_PANELS);

    const [loadControlsHubConfig, { data: controlsHubConfigData }] = useLazyQuery<
        getControlsHubConfigWidgetsForStudy,
        getControlsHubConfigWidgetsForStudyVariables
    >(GET_CONTROLS_HUB_WIDGET_CONFIG);

    useEffect(() => {
        if (orgKey && studyRef)
            getPanels({ variables: { organisationId: orgKey, studyId: studyRef } }).catch((err) =>
                window.console.error(err),
            );
        if (studyRef) {
            loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
            loadControlsHubConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        }
    }, [orgKey, studyRef]);

    return (
        <div className="tab-card">
            <ChipList
                // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                // some rerenders to steal focus from input elements
                key={String(anyComponentHasChanges())}
                className="chip"
                data-testid="manage-hubs-central-monitoring-chip-list"
                defaultData={[
                    {
                        text: tAdmin('chips.studySiteHub'),
                        value: 'StudySiteHub',
                    },
                    {
                        text: tAdmin('chips.controlsHub'),
                        value: 'ControlsHub',
                    },
                ]}
                onChange={async (e) => {
                    if (!e.value) {
                        return;
                    }
                    if (anyComponentHasChanges()) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) {
                            return;
                        }
                        discardChanges();
                    }
                    setSelected(e.value as CentralMonitoringType);
                }}
                value={selected}
                selection="single"
            />
            {selected === 'StudySiteHub' ? (
                <>
                    <div style={{ display: 'flex', flexDirection: 'row', columnGap: '10px', marginBottom: '10px' }}>
                        <div style={{ width: '50%', display: 'flex' }}>
                            <ComparisonDatasetCard studyRef={studyRef} canEdit={canEdit} />
                        </div>
                        <div style={{ width: '50%', display: 'flex' }}>
                            <StudyHubPanelLineCalculationSelector studyRef={studyRef} canEdit={canEdit} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <WidgetConfig
                            data={
                                widgetConfigData?.getStudyDashboardConfigWidgetsForStudy?.widgets as WidgetDataProps[]
                            }
                            canEdit={canEdit}
                            studyRef={studyRef}
                            type="STUDY_DASHBOARD"
                            refetch={async () => {
                                // Force the cache to update. Fixes issues when the initial config is null such as:
                                // 1. Widget data not being updated with id's after save
                                // 2. Saved widgets not rendering when swapping between chips
                                await loadConfig({ variables: { studyId: studyRef } });
                            }}
                        />
                        <PanelsConfig
                            orgKey={orgKey}
                            studyRef={studyRef}
                            canEdit={canEdit}
                            panelData={getPanelsRawData?.panels ?? null}
                        />
                        <StudyHubStudyFields studyRef={studyRef} canEdit={canEdit} />
                        <StudyHubSiteFields studyRef={studyRef} canEdit={canEdit} />
                        <MonitoringToolkitConfig studyRef={studyRef} canEdit={canEdit} />
                    </div>
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <WidgetConfig
                        data={controlsHubConfigData?.getControlsHubConfigWidgetsForStudy?.widgets as WidgetDataProps[]}
                        canEdit={canEdit}
                        studyRef={studyRef}
                        type="CONTROLS_HUB"
                        refetch={async () => {
                            // Force the cache to update. Fixes issues when the initial config is null such as:
                            // 1. Widget data not being updated with id's after save
                            // 2. Saved widgets not rendering when swapping between chips
                            await loadControlsHubConfig({ variables: { studyId: studyRef } });
                        }}
                    />
                    <ControlsHubPanelsConfig
                        orgKey={orgKey}
                        studyRef={studyRef}
                        canEdit={canEdit}
                        panelData={getPanelsRawData?.panels ?? null}
                    />
                </div>
            )}
        </div>
    );
};

export default ManageHubsCentralMonitoring;
